import type { Timestamp } from 'firebase/firestore'

export const TEMPLATE_CATEGORIES = ['Workspaces', 'Designs', 'Automations', 'Forms', 'Checklists'] as const
export type TemplateCategory = (typeof TEMPLATE_CATEGORIES)[number]

export const TEMPLATE_CATEGORY_SLUGS: Record<TemplateCategory, string> = {
  Workspaces: 'workspaces',
  Designs: 'designs',
  Automations: 'automations',
  Forms: 'forms',
  Checklists: 'checklists',
}

export const TEMPLATE_CATEGORY_BY_SLUG = Object.fromEntries(
  TEMPLATE_CATEGORIES.map((category) => [TEMPLATE_CATEGORY_SLUGS[category], category]),
) as Record<string, TemplateCategory>

export type TemplatePreviewData = {
  title: string
  summary: string
  metrics?: Array<{ label: string; value: string | number }>
  sections?: Array<{ title: string; items: string[] }>
  payload?: Record<string, unknown>
}

export type TemplateTier = 'FREE' | 'PREMIUM'
export type ExploreTemplateStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED'

export interface TemplateRegistryEntry {
  id: string
  title: string
  description: string
  category: TemplateCategory
  tags: string[]
  registryVersion: number
  previewData: TemplatePreviewData
  createdAt: Timestamp | Date | null
  updatedAt?: Timestamp | Date | null
  featured?: boolean
  tier?: TemplateTier
  kitRef?: {
    kitSlug: string
    partSlug: string
    kitVersion: number
  }
}

export interface ExploreTemplateDoc extends TemplateRegistryEntry {
  status: ExploreTemplateStatus
  overridesBundledId: string | null
  createdByUid: string
  updatedByUid: string
  publishedAt: Timestamp | Date | null
  previewDataHash?: string
}

export interface SavedTemplateRecord {
  id: string
  workspaceId: string
  templateId: string
  category: TemplateCategory
  title: string
  registryVersion: number
  sourceRegistryVersion: number
  importedBy: string
  importedByName?: string | null
  importedAt: Timestamp | Date | null
  updatedAt: Timestamp | Date | null
}

export interface WorkspaceTemplateClone extends SavedTemplateRecord {
  sourceTemplateId: string
  sourceRegistryVersion: number
  sourcePreviewData: TemplatePreviewData
  previewData: TemplatePreviewData
  isModified: boolean
  modifiedAt?: Timestamp | Date | null
}

export const isTemplateCategory = (value: unknown): value is TemplateCategory =>
  typeof value === 'string' && TEMPLATE_CATEGORIES.includes(value as TemplateCategory)

export const isExploreTemplateStatus = (value: unknown): value is ExploreTemplateStatus =>
  value === 'DRAFT' || value === 'PUBLISHED' || value === 'ARCHIVED'
